/**
 * Category Management Page - Admin Only
 * Single unified interface for managing categories and subcategories
 */

import { useState } from 'react';
import { Plus, FolderKanban, Layers } from 'lucide-react';
import { useCategoryStore, useSubcategoryStore, useDeleteCategory, useDeleteSubcategory } from '../../stores/categories';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { PageHeader } from '../../components/layout/PageHeader';
import { StatCard } from '../../components/ui/stat-card';
import { StatCardSkeleton } from '../../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { CategoryList } from '../../components/admin/CategoryList';
import { CreateCategoryDialog } from '../../components/admin/CreateCategoryDialog';
import { EditCategoryDialog } from '../../components/admin/EditCategoryDialog';
import { CreateSubcategoryDialog } from '../../components/admin/CreateSubcategoryDialog';
import { EditSubcategoryDialog } from '../../components/admin/EditSubcategoryDialog';
import type { Category, Subcategory } from '../../types/api';

export function CategoryManagementPage() {
  const [showCreateCategoryDialog, setShowCreateCategoryDialog] = useState(false);
  const [showEditCategoryDialog, setShowEditCategoryDialog] = useState(false);
  const [showCreateSubcategoryDialog, setShowCreateSubcategoryDialog] = useState(false);
  const [showEditSubcategoryDialog, setShowEditSubcategoryDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'category' | 'subcategory'; id: number; name: string } | null>(null);

  useCategoryStore.live();
  useSubcategoryStore.live();
  const categories = useCategoryStore(s => s.categories);
  const loadingCategories = useCategoryStore(s => s._loading);
  const subcategories = useSubcategoryStore(s => s.subcategories);
  const loadingSubcategories = useSubcategoryStore(s => s._loading);
  const deleteCategoryMutation = useDeleteCategory();
  const deleteSubcategoryMutation = useDeleteSubcategory();

  const isLoading = loadingCategories || loadingSubcategories;

  function handleEditCategory(category: Category) {
    setSelectedCategory(category);
    setShowEditCategoryDialog(true);
  }

  function handleDeleteCategory(category: Category) {
    setDeleteTarget({ type: 'category', id: category.category_id, name: category.category_name });
    setShowDeleteDialog(true);
  }

  function handleCreateSubcategory(category: Category) {
    setSelectedCategory(category);
    setShowCreateSubcategoryDialog(true);
  }

  function handleEditSubcategory(subcategory: Subcategory) {
    setSelectedSubcategory(subcategory);
    setShowEditSubcategoryDialog(true);
  }

  function handleDeleteSubcategory(subcategory: Subcategory) {
    setDeleteTarget({ type: 'subcategory', id: subcategory.subcategory_id, name: subcategory.subcategory_name });
    setShowDeleteDialog(true);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;

    try {
      if (deleteTarget.type === 'category') {
        await deleteCategoryMutation.mutate({ categoryId: deleteTarget.id });
        toast.success(`"${deleteTarget.name}" has been deleted successfully.`);
      } else {
        await deleteSubcategoryMutation.mutate({ subcategoryId: deleteTarget.id });
        toast.success(`"${deleteTarget.name}" has been deleted successfully.`);
      }
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    } catch (error: any) {
      toast.error(error.message || `This ${deleteTarget.type} is in use by assets.`);
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  }

  function handleSuccess() {
    toast.success('Changes saved successfully.');
  }

  return (
    <>
      <PageHeader
        title="Category Management"
        description="Manage asset categories and subcategories"
        breadcrumbs={[
          { label: 'Administration' },
          { label: 'Categories' },
        ]}
        actions={
          <Button onClick={() => setShowCreateCategoryDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Category
          </Button>
        }
      />

      <main className="flex-1 overflow-y-auto bg-muted/40">
        <div className="container mx-auto p-6 lg:p-8">
          <div className="flex flex-col gap-6">
            <div className="grid gap-4 md:grid-cols-2">
              {isLoading ? (
                <>
                  <StatCardSkeleton />
                  <StatCardSkeleton />
                </>
              ) : (
                <>
                  <StatCard
                    title="Total Categories"
                    value={categories?.length || 0}
                    icon={FolderKanban}
                  />
                  <StatCard
                    title="Total Subcategories"
                    value={subcategories?.length || 0}
                    icon={Layers}
                  />
                </>
              )}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Categories & Subcategories</CardTitle>
                <CardDescription>
                  Organize assets into hierarchical categories. Expand categories to view and manage subcategories.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CategoryList
                  categories={categories || []}
                  subcategories={subcategories || []}
                  isLoading={isLoading}
                  onEditCategory={handleEditCategory}
                  onDeleteCategory={handleDeleteCategory}
                  onCreateSubcategory={handleCreateSubcategory}
                  onEditSubcategory={handleEditSubcategory}
                  onDeleteSubcategory={handleDeleteSubcategory}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <CreateCategoryDialog
        open={showCreateCategoryDialog}
        onOpenChange={setShowCreateCategoryDialog}
        onSuccess={handleSuccess}
      />

      <EditCategoryDialog
        open={showEditCategoryDialog}
        onOpenChange={setShowEditCategoryDialog}
        category={selectedCategory}
        onSuccess={handleSuccess}
      />

      <CreateSubcategoryDialog
        open={showCreateSubcategoryDialog}
        onOpenChange={setShowCreateSubcategoryDialog}
        parentCategoryId={selectedCategory?.category_id || 0}
        parentCategoryName={selectedCategory?.category_name || ''}
        onSuccess={handleSuccess}
      />

      <EditSubcategoryDialog
        open={showEditSubcategoryDialog}
        onOpenChange={setShowEditSubcategoryDialog}
        subcategory={selectedSubcategory}
        onSuccess={handleSuccess}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteTarget?.type === 'category' ? 'Category' : 'Subcategory'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
              {deleteTarget?.type === 'category' && ' All subcategories under this category will also be deleted.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
